import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Zap, Star, Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, AlertCircle,
  CreditCard, ChefHat, Globe, BarChart3,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button, Input } from '../components/ui';

const FEATURES = [
  { icon: CreditCard, label: 'Point of Sale' },
  { icon: ChefHat, label: 'Kitchen Display' },
  { icon: Globe, label: 'Online Ordering' },
  { icon: BarChart3, label: 'Analytics & Reports' },
];

const STATS = [
  { value: '1,000+', label: 'Restaurants' },
  { value: '1,800+', label: 'Locations' },
  { value: '4.9', label: 'Rating', star: true },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const backendMsg = err.response?.data?.msg;
      if (backendMsg) {
        setError(backendMsg);
      } else if (err.response) {
        setError(`Server error (HTTP ${err.response.status})`);
      } else if (err.request) {
        setError('Cannot reach server. Check that the backend is running on port 5002.');
      } else {
        setError('Login failed: ' + err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-neutral-50">
      {/* Left brand panel — marketing hero, hidden on small screens */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden text-white p-12 flex-col justify-between"
        style={{ background: 'linear-gradient(135deg, #0A1B3D 0%, #122A5C 55%, #0A1730 100%)' }}
      >
        {/* Decorative glows */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-primary-400/10 blur-3xl pointer-events-none" />
        {/* Dot-grid texture */}
        <div
          className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />

        {/* Brand */}
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1 shadow-sm">
            <img src="/chenab-logo.jpeg" alt="Chenab System" className="w-full h-full object-contain" />
          </div>
          <span className="text-lg font-semibold tracking-tight">Chenab System</span>
        </div>

        {/* Headline */}
        <div className="relative space-y-6 max-w-md">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-medium text-primary-100">
            <Zap size={13} className="text-warning-500" fill="currentColor" />
            Zero commission fees
          </div>
          <h1 className="text-4xl font-bold leading-[1.15] tracking-tight">
            Transform your<br />restaurant business.
          </h1>
          <p className="text-primary-100/90 text-base leading-relaxed">
            The all-in-one platform to run POS, kitchen, delivery and inventory —
            and <span className="font-semibold text-white">boost revenue by up to 40%</span>.
          </p>

          {/* Feature cards */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-xl bg-white/[0.06] border border-white/10 px-3.5 py-3 backdrop-blur-sm"
              >
                <span className="w-8 h-8 rounded-lg bg-primary-500/20 text-primary-200 flex items-center justify-center flex-shrink-0">
                  <Icon size={16} />
                </span>
                <span className="text-sm font-medium text-primary-50">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats + testimonial glass card */}
        <div className="relative rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-5">
          <div className="flex items-center">
            {STATS.map((s, i) => (
              <div key={s.label} className={`flex-1 ${i > 0 ? 'border-l border-white/10 pl-4' : ''}`}>
                <p className="text-2xl font-bold tabular-nums flex items-center gap-1">
                  {s.value}
                  {s.star && <Star size={15} className="text-warning-500" fill="currentColor" />}
                </p>
                <p className="text-xs text-primary-200/80 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 mt-4 pt-4">
            <div className="flex gap-0.5 mb-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={13} className="text-warning-500" fill="currentColor" />
              ))}
            </div>
            <p className="text-sm text-primary-50/90 leading-relaxed">
              “Chenab System paid for itself within the first month — orders, kitchen and stock finally in one place.”
            </p>
            <p className="text-xs text-primary-200/70 mt-1.5">— Tuscany Courtyard</p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-gradient-to-b from-neutral-50 to-neutral-100">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="flex lg:hidden items-center justify-center gap-3 mb-8">
            <div className="w-9 h-9 bg-white border border-neutral-200 rounded-lg flex items-center justify-center p-1 shadow-sm">
              <img src="/chenab-logo.jpeg" alt="Chenab System" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-semibold tracking-tight text-neutral-900">Chenab System</span>
          </div>

          <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] p-8 sm:p-9">
            {/* Heading */}
            <div className="mb-7">
              <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4 ring-1 ring-primary-100">
                <Lock size={20} />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 tracking-tight">Welcome back</h2>
              <p className="text-sm text-neutral-500 mt-1.5">Sign in to your Chenab System dashboard.</p>
            </div>

            {error && (
              <div className="mb-5 p-3 rounded-lg bg-danger-50 border border-danger-100 text-danger-700 text-sm flex items-start gap-2">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Email address"
                type="email"
                leftIcon={<Mail size={16} />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@restaurant.com"
                autoComplete="email"
                required
              />
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                leftIcon={<Lock size={16} />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-neutral-400 hover:text-neutral-700 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-xs font-medium text-primary-600 hover:text-primary-700">
                  Forgot password?
                </Link>
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={submitting}
                rightIcon={!submitting && <ArrowRight size={18} />}
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-7 pt-5 border-t border-neutral-100 flex items-center justify-center gap-1.5 text-xs text-neutral-400">
              <ShieldCheck size={13} className="text-success-500" />
              Secured with 256-bit encryption
            </div>
          </div>

          <p className="text-sm text-neutral-500 text-center mt-6">
            New restaurant?{' '}
            <Link to="/signup" className="text-primary-600 font-medium hover:text-primary-700">Start your free trial</Link>
          </p>
          <p className="text-xs text-neutral-400 text-center mt-2">© 2026 Chenab System · All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
